import { AxiosRequestConfig } from 'axios';
import axiosClient from './axiosClient';
import { IBaseResponse, IListDataResponse } from '~/utils/interface/base';
import { FormValues, ICollectionAdapter } from '~/utils/interface/setup';

interface IOptions<TRow extends { id: string | number }> {
  url: string;
  // GET returns { responses, pagination } unless the endpoint returns a bare array
  paged?: boolean;
  config?: AxiosRequestConfig;
  // POST goes elsewhere than the list/PUT/DELETE url (e.g. files/upload-files)
  createUrl?: string;
  // extra query params on every list call (e.g. a record type)
  listParams?: Record<string, string>;
  rowToForm: (row: TRow) => FormValues;
  toBody: (form: FormValues, isCreate: boolean) => object | FormData;
}

// CRUD adapter for the plain REST endpoints (list / POST / PUT :id / DELETE :id).
export const createJsonAdapter = <TRow extends { id: string | number }>({
  url,
  paged = true,
  config,
  listParams,
  createUrl,
  rowToForm,
  toBody,
}: IOptions<TRow>): ICollectionAdapter<TRow> => {
  const requestConfig = (body: object | FormData): AxiosRequestConfig =>
    body instanceof FormData
      ? { ...config, headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' } }
      : { ...config };

  return {
    serverPaged: paged,
    list: async ({ page, limit, textSearch }) => {
      const resp = await axiosClient.get<IBaseResponse<IListDataResponse<TRow[]> | TRow[]>>(url, {
        ...config,
        params: paged ? { ...listParams, page, limit, textSearch: textSearch || undefined } : undefined,
      });
      const data = resp.data.data;
      return Array.isArray(data)
        ? { rows: data, totalItems: data.length }
        : { rows: data.responses, totalItems: data.pagination.totalItems };
    },
    getId: (row) => String(row.id),
    rowToForm,
    create: (form) => {
      const body = toBody(form, true);
      return axiosClient.post(createUrl ?? url, body, requestConfig(body));
    },
    update: (id, form) => {
      const body = toBody(form, false);
      return axiosClient.put(`${url}/${id}`, body, requestConfig(body));
    },
    remove: (id) => axiosClient.delete(`${url}/${id}`, config),
  };
};
